/**
 * Toast 알림 시스템
 *
 * 전역에서 사용 가능한 가벼운 토스트 알림.
 * - alert() / inline 에러 div 를 대체하기 위한 통일된 피드백 채널
 * - Context 기반: 어디서든 useToast() 호출로 발행
 * - Portal 렌더링: 화면 우상단(또는 모바일 하단)에 누적 표시, 자동 dismiss
 *
 * 트레이드오프:
 * - 외부 의존성(react-hot-toast 등) 없이 자체 구현 — 패키지 단순화 우선
 * - 큐가 길어질 때 스크롤은 제공하지 않음(보통 동시 3개 이내라는 가정)
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface ToastItem {
  id: string
  message: string
  variant: ToastVariant
  duration: number // ms; 0이면 수동 dismiss 까지 유지
}

interface ToastContextValue {
  show: (message: string, options?: { variant?: ToastVariant; duration?: number }) => void
  success: (message: string, duration?: number) => void
  error: (message: string, duration?: number) => void
  info: (message: string, duration?: number) => void
  warning: (message: string, duration?: number) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const VARIANT_STYLES: Record<ToastVariant, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-800 dark:text-green-200',
    icon: '✅',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-900/30',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-800 dark:text-red-200',
    icon: '❌',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-800 dark:text-blue-200',
    icon: 'ℹ️',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/30',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-800 dark:text-yellow-200',
    icon: '⚠️',
  },
}

interface ToastProviderProps {
  children: React.ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  // setTimeout 핸들 추적 — 컴포넌트 unmount / 수동 dismiss 시 정리해야 메모리 누수 방지
  const timersRef = useRef<Map<string, number>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer !== undefined) {
      window.clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const show = useCallback(
    (message: string, options?: { variant?: ToastVariant; duration?: number }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const variant = options?.variant ?? 'info'
      const duration = options?.duration ?? (variant === 'error' ? 5000 : 3000)

      setToasts((prev) => [...prev, { id, message, variant, duration }])

      if (duration > 0) {
        const timer = window.setTimeout(() => dismiss(id), duration)
        timersRef.current.set(id, timer)
      }
    },
    [dismiss]
  )

  // unmount 시 모든 타이머 정리
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((t) => window.clearTimeout(t))
      timers.clear()
    }
  }, [])

  const value: ToastContextValue = {
    show,
    success: (message, duration) => show(message, { variant: 'success', duration }),
    error: (message, duration) => show(message, { variant: 'error', duration }),
    info: (message, duration) => show(message, { variant: 'info', duration }),
    warning: (message, duration) => show(message, { variant: 'warning', duration }),
    dismiss,
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto pointer-events-none"
          aria-live="polite"
          aria-atomic="true"
        >
          {toasts.map((toast) => {
            const styles = VARIANT_STYLES[toast.variant]
            return (
              <div
                key={toast.id}
                role="status"
                className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg ${styles.bg} ${styles.border} ${styles.text} animate-fadeIn`}
              >
                <span className="text-lg leading-none mt-0.5" aria-hidden="true">
                  {styles.icon}
                </span>
                <p className="flex-1 text-sm font-medium break-words">{toast.message}</p>
                <button
                  onClick={() => dismiss(toast.id)}
                  className="flex-shrink-0 -mr-1 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
                  aria-label="알림 닫기"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

/**
 * Toast 발행 훅
 *
 * ToastProvider 하위에서만 사용 가능. 외부에서 호출 시 명시적 에러를 던져
 * "조용히 동작 안 함" 상황을 방지한다.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return ctx
}
